import React, { useState, FormEvent } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, Check, X } from 'lucide-react';
import './ResetPasswordForm.css';

export const ResetPasswordForm: React.FC = () => {
  const { updatePassword, completePasswordRecovery, logout } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const result = await updatePassword(newPassword);

      if (!result.success) {
        setError(result.error || 'Error updating password');
      } else {
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = async () => {
    await logout();
    completePasswordRecovery();
  };

  // Success state
  if (success) {
    return (
      <div className="login-overlay">
        <div className="login-container">
          <div className="login-form">
            <div className="login-header">
              <div className="reset-icon reset-icon--success">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h2>Password Updated!</h2>
              <p>Your password has been changed successfully</p>
            </div>

            <div className="reset-success-body">
              <p className="reset-success-message">
                You can now sign in with your new password.
              </p>
              <button
                type="button"
                className="login-button"
                onClick={handleBackToLogin}
              >
                Go to Login
              </button>
            </div>

            <div className="login-footer">
              <p>RAE Robotics USA Inc. — RAE Project Management</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="login-overlay">
      <div className="login-container">
        <div className="login-form">
          <div className="login-header">
            <div className="reset-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
              <h2>New Password</h2>
              <p>Enter your new password to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form-content">
            <div className="input-group">
              <label htmlFor="new-password">New Password</label>
              <input
                type="password"
                id="new-password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                placeholder="Minimum 6 characters"
                required
                disabled={isLoading}
                autoFocus
                minLength={6}
              />
            </div>

            <div className="input-group">
              <label htmlFor="confirm-password">Confirm Password</label>
              <input
                type="password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="Repeat your password"
                required
                disabled={isLoading}
                minLength={6}
              />
            </div>

            {/* Password strength indicator */}
            {newPassword.length > 0 && (
              <div className="password-strength">
                <div className="password-strength-bar">
                  <div
                    className={`password-strength-fill ${
                      newPassword.length < 6
                        ? 'weak'
                        : newPassword.length < 10
                        ? 'medium'
                        : 'strong'
                    }`}
                    style={{
                      width: `${Math.min((newPassword.length / 12) * 100, 100)}%`,
                    }}
                  />
                </div>
                <span className="password-strength-label">
                  {newPassword.length < 6
                    ? 'Too short'
                    : newPassword.length < 10
                    ? 'Acceptable'
                    : 'Strong'}
                </span>
              </div>
            )}

            {/* Match indicator */}
            {confirmPassword.length > 0 && (
              <div className={`password-match ${newPassword === confirmPassword ? 'match' : 'no-match'}`}>
                {newPassword === confirmPassword
                  ? <><Check size={13} strokeWidth={2.5} /> Passwords match</>
                  : <><X size={13} strokeWidth={2.5} /> Passwords do not match</>
                }
              </div>
            )}

            {error && (
              <div className="error-message">
                <AlertCircle size={15} /> {error}
              </div>
            )}

            <button
              type="submit"
              className="login-button"
              disabled={isLoading || !newPassword || !confirmPassword || newPassword.length < 6}
            >
              {isLoading ? 'Updating...' : 'Update Password'}
            </button>

            <button
              type="button"
              className="reset-cancel-button"
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              Cancel and go back to Login
            </button>
          </form>

          <div className="login-footer">
            <p>RAE Robotics USA Inc. — RAE Project Management</p>
          </div>
        </div>
      </div>
    </div>
  );
};
